import {
  VotingSettings,
  votingOperations,
} from '@app/shared/workflow-voting/voting.models';
import {
  WorkflowDefinitionDocument,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import {
  addWorkflowCapability,
  installedReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import { initializeBlankStep, StepCommand } from './workflow-step-composition';
import {
  providesVotingBlock,
  isHearingPanelPopulation,
  isCombinedVotingRound,
} from '@app/shared/workflow-voting/voting-sources';
export function votingProducers(
  doc: WorkflowDefinitionDocument,
  nodeId: string,
  code: string,
) {
  return doc.nodes.filter(
    (n) =>
      n.typeCode === 'human_step' &&
      n.declaration?.detail.blocks.some((b) => providesVotingBlock(b, code)) &&
      votingProducerIssue(doc, nodeId, n.id) === '',
  );
}
export function votingProducerIssue(
  doc: WorkflowDefinitionDocument,
  consumer: string,
  producer: string,
): string {
  if (consumer === producer) return '';
  const pending = doc.nodes
      .filter((n) => n.typeCode === 'entry')
      .map((n) => n.id),
    seen = new Set<string>();
  while (pending.length) {
    const id = pending.pop()!;
    if (id === producer || seen.has(id)) continue;
    if (id === consumer)
      return 'The source must run on every path before this step.';
    seen.add(id);
    pending.push(
      ...doc.edges
        .filter((e) => e.fromNodeId === id && e.pathKindCode !== 'return')
        .map((e) => e.toNodeId),
    );
  }
  const after = [producer],
    reachable = new Set<string>();
  while (after.length) {
    const id = after.pop()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    after.push(
      ...doc.edges
        .filter((e) => e.fromNodeId === id && e.pathKindCode !== 'return')
        .map((e) => e.toNodeId),
    );
  }
  return reachable.has(consumer)
    ? ''
    : 'Choose a source on this step’s forward path.';
}
export function installVoting(
  doc: WorkflowDefinitionDocument,
  nodeId: string,
  code: string,
  settings: VotingSettings,
  catalog: WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  let next = initializeBlankStep(doc, nodeId);
  if (
    code === 'voting.result' &&
    next.nodes
      .find((n) => n.id === nodeId)
      ?.declaration?.detail.blocks.some(isCombinedVotingRound)
  )
    throw new Error(
      'The combined vote already supplies this step’s quorum result.',
    );
  if (
    code === 'voting.population' &&
    next.nodes
      .find((n) => n.id === nodeId)
      ?.declaration?.detail.blocks.some(isHearingPanelPopulation)
  )
    throw new Error(
      'The hearing panel already supplies this step’s voting population.',
    );
  const commands: StepCommand[] = [];
  for (const [kind, key] of [
    ['view', code],
    ...votingOperations[code].map((op) => ['command', 'voting.' + op]),
  ]) {
    const entry = catalog.filter(
      (c) =>
        c.key.provider === 'voting' &&
        c.key.kind === kind &&
        c.key.code === key &&
        c.key.version === 1 &&
        c.lifecycle === 'available',
    );
    if (entry.length !== 1)
      throw new Error(
        'The exact voting module is unavailable. No draft changes were made.',
      );
    next = addWorkflowCapability(next, entry[0], catalog);
    if (kind === 'command')
      commands.push({
        capability: installedReference(entry[0]),
        requirements: [],
        outcomes: [],
      });
  }
  return {
    ...next,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      next.payloadSchemaVersion,
      '1.8',
    ),
    nodes: next.nodes.map((n) => {
      if (n.id !== nodeId) return n;
      const declaration = n.declaration!,
        existing = declaration.detail.blocks.find(
          (b) => b.code === code && b.provider === 'voting',
        );
      const block = {
        ...existing,
        instanceId:
          existing?.instanceId ??
          code.replaceAll('.', '-') + '-' + crypto.randomUUID(),
        provider: 'voting',
        code,
        version: code === 'voting.ballots' && existing?.version === 2 ? 2 : 1,
        settingsVersion: 1,
        settings: { ...settings },
        region: existing?.region ?? 'main',
        width: 'full' as const,
      };
      return {
        ...n,
        declaration: {
          ...declaration,
          detail: {
            ...declaration.detail,
            blocks: [
              ...declaration.detail.blocks.filter(
                (b) => !(b.provider === 'voting' && b.code === code),
              ),
              block,
            ],
          },
          behavior: {
            ...declaration.behavior,
            commands: [
              ...declaration.behavior.commands,
              ...commands.filter(
                (c) =>
                  !declaration.behavior.commands.some(
                    (old) =>
                      old.capability.key.code === c.capability.key.code &&
                      old.capability.key.provider === 'voting',
                  ),
              ),
            ],
          },
        },
      };
    }),
  };
}
export function applyVotingRoutes(
  doc: WorkflowDefinitionDocument,
  nodeId: string,
  routes: Record<string, string>,
): WorkflowDefinitionDocument {
  const node = doc.nodes.find((n) => n.id === nodeId)!;
  const id = `${nodeId}-voting-result`,
    existing = doc.nodes.find((n) => n.id === id);
  if (existing && existing.routing?.conditionCode !== 'voting.result')
    throw new Error('This outcome routing identity is already in use.');
  const incoming = doc.edges.filter((e) => e.fromNodeId === nodeId);
  if (
    incoming.length !== 1 ||
    (incoming[0].toNodeId !== id &&
      !['human_step', 'terminal'].includes(
        doc.nodes.find((n) => n.id === incoming[0].toNodeId)?.typeCode || '',
      ))
  )
    throw new Error(
      'This step already has a different routing setup. Review its paths before replacing them.',
    );
  if (
    existing &&
    doc.edges.some((e) => e.toNodeId === id && e.fromNodeId !== nodeId)
  )
    throw new Error('A shared outcome router cannot be replaced.');
  const nodes = existing
    ? doc.nodes
    : [
        ...doc.nodes,
        {
          id,
          typeCode: 'routing_decision',
          displayName: 'Voting result',
          displayOrder: node.displayOrder + 1,
          stepCode: null,
          allowedTransitions: [],
          requiredDecisions: [],
          routing: { conditionCode: 'voting.result', conditionVersion: 1 },
        },
      ];
  const outcomes = ['approved', 'rejected', 'no_decision', 'quorum_not_met'];
  if (Object.keys(routes).length !== 4)
    throw new Error('Configure each voting result destination.');
  const edges = [
    ...doc.edges.filter((e) => e.fromNodeId !== nodeId && e.fromNodeId !== id),
    { ...incoming[0], toNodeId: id },
    ...outcomes.map((outcome) => {
      const target = doc.nodes.find(
        (n) =>
          n.id === routes[outcome] &&
          ['human_step', 'terminal'].includes(n.typeCode),
      );
      if (!target || target.id === nodeId)
        throw new Error(
          'Choose an existing destination for each voting result.',
        );
      const pending = [target.id],
        seen = new Set<string>();
      while (pending.length) {
        const current = pending.pop()!;
        if (seen.has(current)) continue;
        seen.add(current);
        pending.push(
          ...doc.edges
            .filter(
              (e) => e.fromNodeId === current && e.pathKindCode !== 'return',
            )
            .map((e) => e.toNodeId),
        );
      }
      const backward = seen.has(nodeId);
      if (backward && target.typeCode !== 'human_step')
        throw new Error('A return must go to a prior work step.');
      return {
        id: `${id}-${outcome}`,
        fromNodeId: id,
        toNodeId: target.id,
        outcomeCode: outcome,
        pathKindCode: backward ? 'return' : null,
      };
    }),
  ];
  if (new Set(edges.map((e) => e.id)).size !== edges.length)
    throw new Error('A voting outcome path identity is already in use.');
  return { ...doc, nodes, edges };
}

/** Removing optional evidence controls must also remove their native commands. */
export function removeVoting(
  doc: WorkflowDefinitionDocument,
  nodeId: string,
  code: string,
): WorkflowDefinitionDocument {
  const referenced = doc.nodes.some(
    (n) =>
      n.declaration?.detail.blocks.some(
        (b) =>
          b.provider === 'voting' &&
          ((code === 'voting.population' &&
            b.code === 'voting.ballots' &&
            b.settings?.['populationNodeId'] === nodeId) ||
            (code === 'voting.ballots' &&
              b.code === 'voting.result' &&
              b.settings?.['roundNodeId'] === nodeId)),
      ) ||
      n.declaration?.behavior.completionRequirements?.some(
        (r) =>
          (code === 'voting.population' &&
            r.provider === 'voting.population-confirmed' &&
            r.settings?.['sourceNodeId'] === nodeId) ||
          (code === 'voting.ballots' &&
            ['voting.round-completed', 'workflow.quorum-evaluated'].includes(
              r.provider,
            ) &&
            r.settings?.['sourceNodeId'] === nodeId) ||
          (code === 'voting.result' &&
            n.id === nodeId &&
            r.provider === 'workflow.quorum-evaluated' &&
            r.version === 2),
      ),
  );
  const routed =
    code === 'voting.result' &&
    doc.edges.some(
      (e) =>
        e.fromNodeId === nodeId &&
        doc.nodes.some(
          (n) =>
            n.id === e.toNodeId && n.routing?.conditionCode === 'voting.result',
        ),
    );
  if (referenced || routed)
    throw new Error(
      'Reconfigure the dependent voting actions, completion requirements or outcome routing before removing this block.',
    );
  const commands = new Set(
    (votingOperations[code] ?? []).map((op) => 'voting.' + op),
  );
  return {
    ...doc,
    nodes: doc.nodes.map((n) =>
      n.id !== nodeId || !n.declaration
        ? n
        : {
            ...n,
            declaration: {
              ...n.declaration,
              detail: {
                ...n.declaration.detail,
                blocks: n.declaration.detail.blocks.filter(
                  (b) => b.provider !== 'voting' || b.code !== code,
                ),
              },
              behavior: {
                ...n.declaration.behavior,
                commands: n.declaration.behavior.commands.filter(
                  (c) =>
                    c.capability.key.provider !== 'voting' ||
                    !commands.has(c.capability.key.code),
                ),
              },
            },
          },
    ),
  };
}
